import React, { useState } from "react";
import { ChevronDown, ChevronRight, User } from "lucide-react";

function getInitials(name) {
  const parts = (name || "?").split(" ").filter(Boolean).slice(0, 2);
  return parts.map((w) => w.charAt(0)).join("").toUpperCase();
}

const Connector = ({ h = 12 }) => <div className="bg-slate-300" style={{ width: "2px", height: h }} />;

export default function OrgTree(props) {
  const { members, deptColorMap, departamentos = [], forwardRef } = props;
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = (key) => setCollapsed((prev) => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const color = (d) => deptColorMap[d] || "#334155";
  const childrenOf = (id) => members.filter((m) => m.jefe_id === id).sort((a, b) => (a.orden || 0) - (b.orden || 0));

  const gerente = members.find((m) => !m.jefe_id) || null;
  const gerDept = gerente ? gerente.departamento : "Dirección";
  const direct = gerente ? childrenOf(gerente.id) : [];
  const asistentes = direct.filter((m) => m.departamento === gerDept);
  const contHead = direct.find((m) => m.departamento === "Continuidad Operativa")
    || members.find((m) => m.departamento === "Continuidad Operativa" && !m.jefe_id)
    || members.find((m) => m.departamento === "Continuidad Operativa")
    || null;
  const deptOrder = departamentos.map((d) => d.name).filter((n) => n !== gerDept && n !== "Continuidad Operativa");
  members.forEach((m) => { if (m.departamento && m.departamento !== gerDept && m.departamento !== "Continuidad Operativa" && !deptOrder.includes(m.departamento)) deptOrder.push(m.departamento); });

  const personCard = (m, variant) => {
    const c = color(m.departamento);
    const vacante = m.estatus === "vacante";
    const head = variant === "head";
    return (
      <div
        key={m.id}
        data-testid={"org-node-" + m.id}
        className={"rounded-md border flex items-start gap-2 px-2.5 py-1.5 w-[220px] " + (vacante ? "border-dashed border-slate-300 bg-white" : "border-slate-200 bg-white")}
        style={head ? { backgroundColor: c, borderColor: c } : { borderLeftWidth: "4px", borderLeftStyle: "solid", borderLeftColor: c }}
      >
        {m.avatar ? (
          <img src={m.avatar} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
        ) : (
          <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-[9px] font-bold text-white" style={{ backgroundColor: head ? "rgba(255,255,255,0.28)" : (vacante ? "#cbd5e1" : c) }}>
            {vacante ? <User className="h-3.5 w-3.5" /> : getInitials(m.nombre)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className={"text-[12px] font-bold leading-tight break-words " + (head ? "text-white" : (vacante ? "text-slate-400 italic" : "text-slate-800"))}>{vacante ? "VACANTE" : m.nombre}</div>
          <div className={"text-[10px] leading-tight break-words " + (head ? "text-white/85" : "text-slate-500")}>{m.puesto}</div>
        </div>
      </div>
    );
  };

  const deptColumn = (deptName) => {
    const c = color(deptName);
    const inDept = members.filter((m) => m.departamento === deptName).sort((a, b) => (a.orden || 0) - (b.orden || 0));
    const head = inDept.find((m) => m.jefe_id === (gerente && gerente.id)) || inDept[0];
    const rest = inDept.filter((m) => head && m.id !== head.id);
    const isCol = collapsed.has(deptName);
    return (
      <div key={deptName} className="flex flex-col items-center shrink-0" data-testid={"org-dept-" + deptName}>
        {/* conector desde el bus superior */}
        <Connector h={14} />
        <button onClick={() => toggle(deptName)} className="rounded-full text-white text-[11px] font-bold uppercase tracking-wide px-4 py-1.5 flex items-center gap-1.5 w-[220px] justify-center" style={{ backgroundColor: c }} data-testid={"org-toggle-" + deptName}>
          {isCol ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {deptName}
        </button>
        {head && (<><Connector /><div>{personCard(head, "head")}</div></>)}
        {!isCol && rest.map((m) => (
          <React.Fragment key={m.id}>
            <Connector />
            {personCard(m, "member")}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const zoneCard = (lead) => {
    const zoneMembers = childrenOf(lead.id);
    return (
      <div key={lead.id} className="flex flex-col items-center shrink-0" data-testid={"org-zone-" + lead.id}>
        {personCard(lead, "head")}
        {zoneMembers.map((m) => (
          <React.Fragment key={m.id}>
            <Connector h={10} />
            {personCard(m, "member")}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const contColor = color("Continuidad Operativa");

  return (
    <div className="bg-white p-6 overflow-x-auto" data-testid="org-tree">
      <div ref={forwardRef} className="bg-white p-6 min-w-max flex flex-col items-center">
        {/* Dirección */}
        {gerente && (
          <div className="flex flex-col items-center">
            {personCard(gerente, "head")}
            {asistentes.map((m) => (
              <React.Fragment key={m.id}>
                <Connector />
                {personCard(m, "member")}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* bajante central + bus horizontal */}
        <Connector h={18} />
        <div className="relative border-t-2 border-slate-300 flex justify-center gap-6 items-start pt-0 w-full">
          {/* Tronco central que conecta el bus principal con Continuidad Operativa */}
          {contHead && <div className="absolute bg-slate-300" style={{ left: "50%", top: 0, bottom: 0, width: "2px", transform: "translateX(-1px)", zIndex: 0 }} />}
          {deptOrder.map((d) => deptColumn(d))}
        </div>

        {/* Continuidad Operativa (conectada al diagrama principal) */}
        {contHead && (
          <div className="w-full flex flex-col items-center">
            <div className="rounded-full bg-white shrink-0" style={{ width: "16px", height: "16px", border: `4px solid ${contColor}`, zIndex: 1 }} />
            <Connector h={10} />
            <button onClick={() => toggle("__cont__")} className="rounded-full text-white text-[11px] font-bold uppercase tracking-wide px-5 py-1.5 flex items-center gap-1.5" style={{ backgroundColor: contColor }} data-testid="org-toggle-continuidad">
              {collapsed.has("__cont__") ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Continuidad Operativa
            </button>
            {!collapsed.has("__cont__") && (
              <div className="mt-3 w-full rounded-md p-4 border-2" style={{ borderColor: contColor, backgroundColor: "#f8fafc" }} data-testid="org-continuidad">
                <div className="flex flex-wrap justify-center gap-5 items-start">
                  {childrenOf(contHead.id).map((z) => zoneCard(z))}
                  {(() => {
                    const zoneLeads = childrenOf(contHead.id);
                    const rendered = new Set([contHead.id]);
                    zoneLeads.forEach((z) => { rendered.add(z.id); childrenOf(z.id).forEach((c) => rendered.add(c.id)); });
                    const orphans = members.filter((m) => m.departamento === "Continuidad Operativa" && !rendered.has(m.id));
                    return orphans.map((m) => (
                      <div key={m.id} className="flex flex-col items-center shrink-0">{personCard(m, "member")}</div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
